import '@sparrow-marketing/sparrow-charts/dist/index.css';
import '@sparrow-marketing/storybook/dist/index.css';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import './app.css';
import { GoogleDrivePicker } from './components/google';
import {
  APP_ROUTE,
  environment,
  ENVIRONMENTS,
  LOCAL_STORAGE_TYPES,
} from './constants';
import {
  initializeExternalTools,
  trackPage,
} from './helpers/external-tools-helper';
import { useInitializeApp, useIsAuthenticated } from './hooks/api/auth';
import { useSetPermissions } from './hooks/api/user';
import { initDB } from './hooks/indexed-db/use-indexed-db';
import useLocalStorage from './hooks/localstorage';
import useInsightsDateRange from './hooks/set-insights-date-range';
import useScrollBarWidth from './hooks/use-scroll-bar-width';
import {
  AddonContainer,
  ApplicationContainer,
  ClientContainer,
  UserContainer,
} from './providers';
import PagesContainer, { PageType } from './providers/pages';
import Routes from './routes';
import {
  InsightsDateRange,
  IsLoader,
  useCampaignFilter,
  useDestinations,
} from './states';
import { useInsights } from './states/insights/hooks';
import { usePaddle } from './hooks/usePaddle';
import { Toaster } from './components/ui/toaster';
import NotificationBar from './components/notification-bar';
import { useGetReports } from './hooks/api/reports';
import { ProviderWrapper } from './providers/helpers/provider-wrapper';
import { useGetPages } from './hooks/api/pages';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from './components/ui/dialog';
import { Button } from './components/ui/button';
import { useChangeBusiness } from './hooks/api/business';
import { SwitchBusinessModal } from './components/switch-business-modal';
import VerifyEmailTopbar from './views/auth/verify-email/verify-email-topbar';
import ToltAffiliate from './components/ToltAffiliate';

// initialize our 3rd party libraries
initializeExternalTools();

initDB({ name: 'store', dbVersion: 1 });

// Add these types at the top of the file
type BusinessAccess = {
  id: number;
  businessId: number;
  userId: number;
  isPrimary: boolean;
  invitationDate: string | null;
  invitedByUserId: number | null;
  disabled: null;
  acceptedInviteDate: string | null;
  createdAt: string;
  updatedAt: string;
  user: { email: string; name: string } | null;
};

type BusinessClient = {
  id: number;
  businessId: number;
  logo: string | null;
  name: string;
  email: string | null;
  timezone: string;
  disabled: null;
  createdAt: string;
  updatedAt: string;
};

type BusinessBilling = {
  sources: number;
  team: boolean;
  workspaces: boolean;
  isLegacyUser: boolean;
  paddleSubscription: {
    status: string;
    planName: string;
    scheduledChange: Record<string, unknown>;
    billingPeriod: string;
    subscriptionEndDate: string;
    subscriptionPriceId: string;
    subscriptionId: string;
  };
};

type BusinessOption = {
  access: BusinessAccess;
  clients: BusinessClient[];
  billing: BusinessBilling;
  id: number;
  primaryUserId: number;
  name: string;
  industry: string | null;
  logo: string | null;
  bucket: string;
  employeeCount: string;
  website: string;
  businessType: string | null;
  businessDescription: string;
  activationFlow: boolean;
  createdAt: string;
  updatedAt: string;
};

// @deprecated - Using NewApp instead
// const App = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { get, set } = useLocalStorage();
//   const isAuthenticated = useIsAuthenticated();
//   const initializeApp = useInitializeApp();
//   const { getInsights } = useInsights();
//   const setCurrentBusinessPermission = useSetPermissions();
//   const changeInsightsDateRange = useInsightsDateRange();
//   usePaddle({});

//   const { setIsAddon } = AddonContainer.useContainer();
//   const dateRange = useRecoilValue(InsightsDateRange);

//   const { setApplicationLoaded } = ApplicationContainer.useContainer();

//   const { fetchFromIndexedDB } = useCampaignFilter();

//   useEffect(() => {
//     let apiSubscribed = true;
//     if (apiSubscribed) {
//       fetchFromIndexedDB();
//     }
//     return () => {
//       apiSubscribed = false;
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const {
//     state: clientState,
//     updateClientAccounts,
//   } = ClientContainer.useContainer();
//   const { state: pageState } = PagesContainer.useContainer();
//   const { state: userState } = UserContainer.useContainer();

//   useEffect(() => {
//     if (
//       userState?.business !== (null || undefined) &&
//       userState?.permissions !== (null || undefined)
//     ) {
//       if (
//         userState?.business?.id?.toString() !==
//         userState?.permissions?.businessId
//       ) {
//         setCurrentBusinessPermission(userState?.business?.id);
//       }
//     }
//   }, [setCurrentBusinessPermission, userState.business, userState.permissions]);

//   /*
//    * This hook is mandatory to load all the insights of the selected client when application is loading.
//    * When we route to email, website, ads, socials, so the insights will be available at that instance
//    */

//   useEffect(() => {
//     let start1: string = dateRange.start;
//     let end1: string = dateRange.end;
//     if (!dateRange.start || !dateRange.end) {
//       const date: any = get(LOCAL_STORAGE_TYPES.WORKSPACE);
//       start1 = date?.start;
//       end1 = date?.end;
//     }
//     if (clientState?.selectedClient?.id && start1 && end1) {
//       getInsights({
//         clientId: clientState.selectedClient?.id,
//         start: start1,
//         end: end1,
//       });
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [clientState.selectedClient?.id]);

//   useEffect(() => {
//     if (clientState.selectedClient?.id) {
//       const availablePages = pageState.pages?.filter((acc: PageType) => {
//         if (acc.clientId !== null && clientState.selectedClient?.id) {
//           return (
//             acc.clientId.toString() === clientState.selectedClient.id.toString()
//           );
//         }
//         return false;
//       });
//       if (
//         clientState?.selectedClient?.accounts &&
//         availablePages &&
//         clientState?.selectedClient?.accounts?.length !== availablePages?.length
//       ) {
//         updateClientAccounts(availablePages);
//       }
//     }
//   }, [clientState.selectedClient, pageState.pages, updateClientAccounts]);

//   useScrollBarWidth();

//   // track all page changes
//   useEffect(
//     function trackPageChange() {
//       // setting the social auth token to the local storage
//       set(LOCAL_STORAGE_TYPES.SOCIAL_AUTH_TOKEN, location.hash);

//       trackPage(location.pathname);
//       history.listen(newLocation => {
//         if (environment === ENVIRONMENTS.production) {
//           window.$crisp.push(['do', 'session:reset']);
//         }
//         trackPage(newLocation.pathname);
//       });
//       const isAddonPath = window.location.pathname.includes('addon');
//       if (isAddonPath) {
//         const {
//           redirect_uri: redirectUri = userState.redirectURL ||
//             window.location.origin,
//           state = null,
//           response_type: responseType = null,
//         } = qs.parse(location?.search?.replaceAll?.('?', '') ?? '') ?? {
//           redirect_uri: window.location.origin,
//           state: null,
//           response_type: null,
//         };
//         const sanitizedRedirectURIs = redirectUri.split('?');
//         const url = `${sanitizedRedirectURIs[0]}?${sanitizedRedirectURIs[1]}`;

//         localStorage.setItem(
//           LOCAL_STORAGE_TYPES.ADDON,
//           JSON.stringify({ redirectUri: url, state, responseType })
//         );
//       }
//       setIsAddon(isAddonPath);
//     },
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//     [userState.redirectURL, location.pathname]
//   );

//   // Track page changes on location updates
//   useEffect(() => {
//     if (environment === ENVIRONMENTS.production) {
//       window.$crisp.push(['do', 'session:reset']);
//     }
//     trackPage(location.pathname);
//   }, [location.pathname]);

//   useEffect(function initAppCalls() {
//     if (isAuthenticated()) {
//       setApplicationLoaded(false);
//       (async function loadApp(): Promise<void> {
//         await initializeApp();
//         setApplicationLoaded(true);
//       })();
//     } else {
//       setApplicationLoaded(true);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const addFocusRing = (event: KeyboardEvent): void => {
//     if (event.key === 'Tab' || event.code === 'Tab') {
//       document.body.classList.remove('App--noOutlineOnFocus');
//     }
//   };

//   const removeFocusRing = (): void => {
//     document.body.classList.add('App--noOutlineOnFocus');
//   };

//   useEffect(() => {
//     document.body.classList.add('App--noOutlineOnFocus');
//     window.addEventListener('keydown', addFocusRing);
//     window.addEventListener('mousedown', removeFocusRing);
//     return (): void => {
//       // remove the event listeners
//       window.removeEventListener('keydown', addFocusRing);
//       window.removeEventListener('mousedown', removeFocusRing);
//     };
//   }, []);

//   useEffect(() => {
//     changeInsightsDateRange();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // scroll to top on route change
//   useEffect(
//     function scrollToTop() {
//       window.scrollTo({
//         top: 0,
//         behavior: 'smooth',
//       });
//     },
//     [location.pathname]
//   );

//   const setIsLoader = useSetRecoilState(IsLoader);

//   useEffect(() => {
//     setIsLoader(true);
//   }, [setIsLoader]);

//   return (
//     <>
//       <VerifyEmailTopbar />
//       <Routes />
//       <GoogleDrivePicker />
//       <Toaster />
//     </>
//   );
// };

const LegacyAppProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { get, set } = useLocalStorage();
  const isAuthenticated = useIsAuthenticated();
  const initializeApp = useInitializeApp();
  // const { getInsights } = useInsights();
  // const { getDestinations } = useDestinations();
  const getReports = useGetReports();
  const getPages = useGetPages();
  const setCurrentBusinessPermission = useSetPermissions();
  // const changeInsightsDateRange = useInsightsDateRange();
  const changeBusiness = useChangeBusiness();
  usePaddle({});

  const { setIsAddon } = AddonContainer.useContainer();
  const dateRange = useRecoilValue(InsightsDateRange);

  const { setApplicationLoaded } = ApplicationContainer.useContainer();

  const { fetchFromIndexedDB } = useCampaignFilter();

  useEffect(() => {
    let apiSubscribed = true;
    if (apiSubscribed) {
      fetchFromIndexedDB();
    }
    return () => {
      apiSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    state: clientState,
    updateClientAccounts,
  } = ClientContainer.useContainer();
  const { state: pageState } = PagesContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  useEffect(() => {
    if (userState?.business != null && userState?.permissions != null) {
      if (
        userState?.business?.id?.toString() !==
        userState?.permissions?.businessId
      ) {
        setCurrentBusinessPermission(userState?.business?.id);
      }
    }
  }, [setCurrentBusinessPermission, userState.business, userState.permissions]);

  /*
   * This hook is mandatory to load all the insights of the selected client when application is loading.
   * When we route to email, website, ads, socials, so the insights will be available at that instance
   */

  useEffect(() => {
    let start1: string = dateRange.start;
    let end1: string = dateRange.end;
    if (!dateRange.start || !dateRange.end) {
      const date: any = get(LOCAL_STORAGE_TYPES.WORKSPACE);
      start1 = date?.start;
      end1 = date?.end;
    }
    if (clientState?.selectedClient?.id && start1 && end1) {
      // getInsights({
      //   clientId: clientState.selectedClient?.id,
      //   start: start1,
      //   end: end1,
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientState.selectedClient?.id]);

  useEffect(() => {
    if (clientState.selectedClient?.id) {
      const availablePages = pageState.pages?.filter((acc: PageType) => {
        if (acc.clientId !== null && clientState.selectedClient?.id) {
          return (
            acc.clientId.toString() === clientState.selectedClient.id.toString()
          );
        }
        return false;
      });
      if (
        clientState?.selectedClient?.accounts &&
        availablePages &&
        clientState?.selectedClient?.accounts?.length !== availablePages?.length
      ) {
        updateClientAccounts(availablePages);
      }
    }
  }, [clientState.selectedClient, pageState.pages, updateClientAccounts]);

  useScrollBarWidth();

  // track all page changes
  useEffect(
    function trackPageChange() {
      // setting the social auth token to the local storage
      set(LOCAL_STORAGE_TYPES.SOCIAL_AUTH_TOKEN, location.hash);

      trackPage(location.pathname);
      // history.listen(newLocation => {
      //   if (environment === ENVIRONMENTS.production) {
      //     window.$crisp.push(['do', 'session:reset']);
      //   }
      //   trackPage(newLocation.pathname);
      // });
      const isAddonPath = window.location.pathname.includes('addon');
      if (isAddonPath) {
        const {
          redirect_uri: redirectUri = userState.redirectURL ||
            window.location.origin,
          state = null,
          response_type: responseType = null,
        } = qs.parse(location?.search?.replaceAll?.('?', '') ?? '') ?? {
          redirect_uri: window.location.origin,
          state: null,
          response_type: null,
        };
        const sanitizedRedirectURIs = redirectUri.split('?');
        const url = `${sanitizedRedirectURIs[0]}?${sanitizedRedirectURIs[1]}`;

        localStorage.setItem(
          LOCAL_STORAGE_TYPES.ADDON,
          JSON.stringify({ redirectUri: url, state, responseType })
        );
      }
      setIsAddon(isAddonPath);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.redirectURL, location.pathname]
  );

  // Track page changes on location updates
  useEffect(() => {
    if (environment === ENVIRONMENTS.production) {
      window.$crisp.push(['do', 'session:reset']);
    }
    trackPage(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (clientState?.selectedClient?.id) {
      // getReports({
      //   clientId: clientState.selectedClient?.id,
      //   fields: 'account,schedule',
      //   $skip: 0,
      //   limit: 100,
      //   '$sort[createdAt]': '-1',
      // });
    }
  }, [clientState.selectedClient?.id]);

  const addFocusRing = (event: KeyboardEvent): void => {
    if (event.key === 'Tab' || event.code === 'Tab') {
      document.body.classList.remove('App--noOutlineOnFocus');
    }
  };

  const removeFocusRing = (): void => {
    document.body.classList.add('App--noOutlineOnFocus');
  };

  useEffect(() => {
    document.body.classList.add('App--noOutlineOnFocus');
    window.addEventListener('keydown', addFocusRing);
    window.addEventListener('mousedown', removeFocusRing);
    return (): void => {
      // remove the event listeners
      window.removeEventListener('keydown', addFocusRing);
      window.removeEventListener('mousedown', removeFocusRing);
    };
  }, []);

  // useEffect(() => {
  //   changeInsightsDateRange();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // scroll to top on route change
  useEffect(
    function scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    [location.pathname]
  );

  // const setIsLoader = useSetRecoilState(IsLoader);

  // useEffect(() => {
  //   setIsLoader(true);
  // }, [setIsLoader]);

  return (
    <>
      {children}
      <ToltAffiliate />
      {clientState?.selectedClient?.id && <SwitchBusinessModal />}
      <GoogleDrivePicker />
      <Toaster />
      <NotificationBar />
    </>
  );
};

const NewApp = () => <Routes />;

export { LegacyAppProvider };
export default NewApp;
